/**
 * Rule-based guidance hint and verification suggestion generator.
 *
 * All wording is strictly pattern/comparison/next-step.
 * NEVER implies likely cause, component, or repair path.
 */

import type { OptiBaySessionEvidence, OptiBayEvidenceType } from '@/types/optibay';

export interface GuidanceHint {
  type: 'pattern_hint' | 'comparison_prompt' | 'next_verification';
  title: string;
  body: string;
}

export interface VerificationSuggestion {
  testName: string;
  description: string;
}

// ── Guidance hints (evidence → interpretation support) ──────────────────

export function generateGuidanceHints(evidence: OptiBaySessionEvidence[]): GuidanceHint[] {
  if (evidence.length === 0) return [];

  const hints: GuidanceHint[] = [];
  const types = new Set(evidence.map(e => e.evidence_type));
  const testPathCounts = new Map<string, number>();
  evidence.forEach(e => {
    testPathCounts.set(e.session_test_path_id, (testPathCounts.get(e.session_test_path_id) || 0) + 1);
  });

  // Waveform captured → prompt known-good comparison
  if (types.has('waveform')) {
    hints.push({
      type: 'comparison_prompt',
      title: 'Waveform Comparison',
      body: 'Compare your captured waveform against the expected pattern for this system. Look for shape, amplitude, and timing differences.',
    });
  }

  // Scan data captured → prompt code cross-reference
  if (types.has('scan_data')) {
    hints.push({
      type: 'pattern_hint',
      title: 'Scan Data Cross-Reference',
      body: 'Review captured scan data for related codes across multiple modules. Patterns across systems may indicate a shared circuit or supply.',
    });
  }

  // Alignment data captured → prompt before/after review
  if (types.has('alignment')) {
    const alignItems = evidence.filter(e => e.evidence_type === 'alignment');
    if (alignItems.length > 0) {
      hints.push({
        type: 'comparison_prompt',
        title: 'Alignment Deviation Review',
        body: 'Review before and after alignment values for significant deviation. Compare against manufacturer specifications.',
      });
    }
  }

  // Meter readings → prompt spec comparison
  if (types.has('meter_reading')) {
    hints.push({
      type: 'comparison_prompt',
      title: 'Meter Reading Verification',
      body: 'Compare meter readings against known-good reference values for this system. Note any readings outside the expected range.',
    });
  }

  // Boolean evidence with false results → prompt investigation
  const falseChecks = evidence.filter(e => e.evidence_type === 'boolean' && e.value_boolean === false);
  if (falseChecks.length > 0) {
    const labels = falseChecks.map(e => e.label).filter(Boolean).join(', ');
    hints.push({
      type: 'next_verification',
      title: 'Negative Check Results',
      body: `One or more checks returned "No"${labels ? ` (${labels})` : ''}. Consider verifying related inputs and conditions before continuing.`,
    });
  }

  // Multiple numeric readings present → prompt pattern observation
  const numericItems = evidence.filter(e => e.evidence_type === 'numeric' || e.evidence_type === 'meter_reading');
  if (numericItems.length >= 2) {
    hints.push({
      type: 'pattern_hint',
      title: 'Multiple Measurements Available',
      body: 'Multiple numeric measurements have been captured. Look for patterns or correlations between readings across test points.',
    });
  }

  // Multiple evidence items on same test path → suggest different method
  const heavyPaths = [...testPathCounts.entries()].filter(([_, count]) => count >= 3);
  if (heavyPaths.length > 0) {
    hints.push({
      type: 'next_verification',
      title: 'Thorough Test Path Coverage',
      body: 'Multiple evidence items exist on the same test path. Consider confirming findings with a different measurement method for cross-validation.',
    });
  }

  return hints;
}

// ── Verification test suggestions (evidence → recommended next tests) ──

export function generateVerificationSuggestions(evidence: OptiBaySessionEvidence[]): VerificationSuggestion[] {
  if (evidence.length === 0) return [];

  const suggestions: VerificationSuggestion[] = [];
  const types = new Set(evidence.map(e => e.evidence_type));

  // Boolean "No" results → suggest follow-up check
  const falseChecks = evidence.filter(e => e.evidence_type === 'boolean' && e.value_boolean === false);
  for (const check of falseChecks.slice(0, 2)) {
    suggestions.push({
      testName: `Verify: ${check.label || 'Failed check'} — related input`,
      description: `The check "${check.label || 'condition'}" returned No. Verify the related input or supply to this point.`,
    });
  }

  // Waveform captured → suggest recheck under load
  if (types.has('waveform')) {
    suggestions.push({
      testName: 'Waveform recheck under load',
      description: 'Capture the same waveform while the system is under load and compare to the initial capture.',
    });
  }

  // Scan data → suggest clear-and-recheck
  if (types.has('scan_data')) {
    suggestions.push({
      testName: 'Clear codes and road test',
      description: 'Clear stored codes, perform a road test, then rescan to confirm which codes return.',
    });
  }

  // Alignment → suggest post-adjustment recheck
  if (types.has('alignment')) {
    suggestions.push({
      testName: 'Post-adjustment alignment recheck',
      description: 'After any adjustments, re-measure alignment values and compare to the before readings.',
    });
  }

  // Meter reading → suggest comparison at different operating condition
  if (types.has('meter_reading')) {
    suggestions.push({
      testName: 'Meter reading at alternate condition',
      description: 'Take the same meter reading under a different operating condition (e.g., warm vs. cold, idle vs. loaded) for comparison.',
    });
  }

  // Multiple evidence on same test path → cross-validate
  const testPathCounts = new Map<string, number>();
  evidence.forEach(e => testPathCounts.set(e.session_test_path_id, (testPathCounts.get(e.session_test_path_id) || 0) + 1));
  const heavyPaths = [...testPathCounts.entries()].filter(([_, count]) => count >= 3);
  if (heavyPaths.length > 0) {
    suggestions.push({
      testName: 'Cross-validate with alternate method',
      description: 'Multiple measurements exist on the same test path. Confirm with a different tool or measurement technique.',
    });
  }

  return suggestions;
}
